import QRCode from 'qrcodejs2-fixes';

// 类型定义
interface QRCodeOptions {
	text: string;
	width?: number;
	height?: number;
	colorDark?: string;
	colorLight?: string;
	correctLevel?: number;
}

interface QRWithIconOptions extends QRCodeOptions {
	icon?: string;
	iconSize?: number; // 0~1 比例
	iconRadius?: number;
	iconMargin?: number; // 新增：图标白边宽度（像素）
	iconBgColor?: string; // 新增：白边背景颜色
}

interface ImagePosition {
	x: number;
	y: number;
	size: number;
	totalSize: number; // 新增：包含白边的总尺寸
}

export class QRCodeGenerator {
	/**
	 * 生成基础二维码Base64
	 */
	static async generateBase64(text: string, options?: Partial<QRCodeOptions>): Promise<string> {
		return new Promise((resolve, reject) => {
			try {
				const container = document.createElement('div');

				new QRCode(container, {
					text,
					width: options?.width || 200,
					height: options?.height || 200,
					colorDark: options?.colorDark || '#000000',
					colorLight: options?.colorLight || '#ffffff',
					correctLevel: options?.correctLevel ?? QRCode.CorrectLevel.H,
				});

				const canvas = container.querySelector('canvas') as HTMLCanvasElement;
				const base64 = canvas.toDataURL('image/png');
				this.cleanupResources(container, canvas);
				resolve(base64);
			} catch (error) {
				reject(new Error(`二维码生成失败: ${error instanceof Error ? error.message : String(error)}`));
			}
		});
	}

	/**
	 * 生成带图标的二维码
	 */
	static async generateWithIcon(options: QRWithIconOptions): Promise<string> {
		if (!options.icon) {
			return this.generateBase64(options.text, options);
		}

		try {
			const base64 = await this.generateBase64(options.text, options);
			const canvas = await this.createCanvasFromBase64(base64);
			await this.drawIconOnCanvas(canvas, options);
			return canvas.toDataURL('image/png');
		} catch (error) {
			throw new Error(`图标二维码生成失败: ${error instanceof Error ? error.message : String(error)}`);
		}
	}

	private static async createCanvasFromBase64(base64: string): Promise<HTMLCanvasElement> {
		return new Promise((resolve, reject) => {
			const img = new Image();
			img.onload = () => {
				const canvas = document.createElement('canvas');
				canvas.width = img.width;
				canvas.height = img.height;
				const ctx = canvas.getContext('2d')!;
				ctx.drawImage(img, 0, 0);
				resolve(canvas);
			};
			img.onerror = () => reject(new Error('基础二维码图片加载失败'));
			img.src = base64;
		});
	}

	private static async drawIconOnCanvas(canvas: HTMLCanvasElement, options: QRWithIconOptions): Promise<void> {
		return new Promise((resolve, reject) => {
			const iconImg = new Image();
			iconImg.crossOrigin = 'Anonymous';

			iconImg.onload = () => {
				try {
					const ctx = canvas.getContext('2d')!;
					const {
						totalSize, // 包含白边的总尺寸
						bgX, // 背景起始X
						bgY, // 背景起始Y
						iconX, // 图标起始X
						iconY, // 图标起始Y
						iconSize, // 图标实际尺寸
					} = this.calculateIconPosition(canvas.width, options);

					// 1. 绘制白边背景
					ctx.save();
					this.createRoundedClip(
						ctx,
						bgX,
						bgY,
						totalSize,
						totalSize,
						(options.iconRadius || 8) + (options.iconMargin || 0) // 扩大圆角
					);
					ctx.fillStyle = options.iconBgColor || '#ffffff';
					ctx.fill();
					ctx.restore();

					// 2. 绘制图标
					ctx.save();
					this.createRoundedClip(ctx, iconX, iconY, iconSize, iconSize, options.iconRadius || 8);
					ctx.drawImage(iconImg, iconX, iconY, iconSize, iconSize);
					ctx.restore();

					resolve();
				} catch (error) {
					reject(error);
				}
			};
			iconImg.onerror = () => reject(new Error('图标资源加载失败'));
			iconImg.src = options.icon!;
		});
	}

	private static calculateIconPosition(
		qrSize: number,
		options: QRWithIconOptions
	): {
		totalSize: number;
		bgX: number;
		bgY: number;
		iconX: number;
		iconY: number;
		iconSize: number;
	} {
		const iconScale = Math.min(0.3, Math.max(0.1, options.iconSize ?? 0.2));
		const margin = options.iconMargin || 0;

		const totalSize = qrSize * iconScale;
		const iconSize = totalSize - margin * 2;

		return {
			totalSize,
			bgX: (qrSize - totalSize) / 2,
			bgY: (qrSize - totalSize) / 2,
			iconX: (qrSize - totalSize) / 2 + margin,
			iconY: (qrSize - totalSize) / 2 + margin,
			iconSize,
		};
	}

	private static createRoundedClip(
		ctx: CanvasRenderingContext2D,
		x: number,
		y: number,
		width: number,
		height: number,
		radius: number
	): void {
		// 参数校验
		radius = Math.min(radius, Math.min(width, height) / 2);

		ctx.beginPath();
		ctx.moveTo(x + radius, y);
		ctx.lineTo(x + width - radius, y);
		ctx.arcTo(x + width, y, x + width, y + radius, radius); // 右上角
		ctx.lineTo(x + width, y + height - radius);
		ctx.arcTo(x + width, y + height, x + width - radius, y + height, radius); // 右下角
		ctx.lineTo(x + radius, y + height);
		ctx.arcTo(x, y + height, x, y + height - radius, radius); // 左下角
		ctx.lineTo(x, y + radius);
		ctx.arcTo(x, y, x + radius, y, radius); // 左上角
		ctx.closePath();
	}

	private static cleanupResources(container: HTMLDivElement, canvas: HTMLCanvasElement): void {
		container.remove();
		canvas.width = 0;
		canvas.height = 0;
	}
}
